/**
 * Fields allowed for direct "Salvează în listă" (no product-code / price impact):
 * - Detalii produs
 * - Structură: sistem / subsistem (+ chapter/subchapter display names refreshed with systems)
 */
export const PRODUCT_DETAILS_KEYS = [
    'productName',
    'measurementUnit',
    'weight',
    'nomenclatureCode',
    'commercialPolicyDiscount',
    'productNotes',
    'isCompositeProduct',
    'componentProductCodes',
    'componentProductsKeepInList',
    'visibleInOfferApp',
    'status',
    'systems',
    'systemSubcategories',
    // Side-effect of primary-system change; codes stay unchanged.
    'chapterName',
    'subchapterName',
];

const PRODUCT_DETAILS_KEY_SET = new Set(PRODUCT_DETAILS_KEYS);

/**
 * Furnizor fields that do not change the selling price and may be saved
 * directly in the list. Adaos referință is informational unless
 * „Adaos = referință” is locked, in which case actual follows it.
 * Denumire furnizor sits in Detalii produs but is stored on the furnizor tab.
 */
export const DIRECT_LIST_SAVE_FURNIZOR_KEYS = [
    'notes',
    'paymentTermDays',
    'manufacturerProductName',
    'manufacturerProductCode',
    'markupReference',
];

const DIRECT_LIST_SAVE_FURNIZOR_KEY_SET = new Set(DIRECT_LIST_SAVE_FURNIZOR_KEYS);

/**
 * @param {string} fieldKey
 * @returns {boolean}
 */
export function isProductDetailsDirectSaveField(fieldKey) {
    return PRODUCT_DETAILS_KEY_SET.has(String(fieldKey || '').trim());
}

/**
 * @param {string} fieldKey
 * @returns {boolean}
 */
export function isDirectListSaveFurnizorField(fieldKey) {
    return DIRECT_LIST_SAVE_FURNIZOR_KEY_SET.has(String(fieldKey || '').trim());
}

/**
 * Propagate-to-peers fields that may be written straight into the price list.
 * Price fields (transport, monedă, adaos, taxă de timbru) stay on the working list.
 * @param {string} fieldKey
 * @returns {boolean}
 */
export function isDirectListPropagateField(fieldKey) {
    const key = String(fieldKey || '').trim();
    return isProductDetailsDirectSaveField(key) || isDirectListSaveFurnizorField(key);
}

/**
 * Direct list save when the edit is only Detalii produs and/or
 * note furnizor, termen de plată, denumire furnizor, adaos referință.
 * @param {Object} params
 * @param {string[]} [params.detailKeys]
 * @param {string[]} [params.manualPricingKeys]
 * @param {string[]} [params.otherProductKeys]
 * @param {boolean} [params.furnizorIdentityChanged]
 * @param {boolean} [params.newFurnizorAdded]
 * @returns {boolean}
 */
export function canSaveDirectGivenChanges({
    detailKeys = [],
    manualPricingKeys = [],
    otherProductKeys = [],
    furnizorIdentityChanged = false,
    newFurnizorAdded = false,
} = {}) {
    if (furnizorIdentityChanged || newFurnizorAdded) {
        return false;
    }
    if ((otherProductKeys || []).length) {
        return false;
    }
    // Unchecking „Adaos = referință” is not a price edit. It must not keep
    // Salvează în listă off after adaos referință itself was changed.
    const manual = (Array.isArray(manualPricingKeys) ? manualPricingKeys : [])
        .filter((key) => key !== 'lockMarkupToReference');
    if (manual.some((key) => !isDirectListSaveFurnizorField(key))) {
        return false;
    }
    const details = Array.isArray(detailKeys) ? detailKeys : [];
    return details.length > 0 || manual.length > 0;
}

/**
 * Direct list save is allowed only when every changed form key is allowlisted.
 * @param {string[]} changedKeys
 * @returns {boolean}
 */
export function changedKeysAllowDirectListSave(changedKeys = []) {
    const keys = Array.isArray(changedKeys) ? changedKeys : [];
    if (!keys.length) {
        return false;
    }
    return keys.every((key) => isProductDetailsDirectSaveField(key));
}
